import type { FC } from 'react';
import React from 'react';
import { Button, Container, Grid } from '@mui/material';
import { HeaderBreadcrumbs, Stack } from '@pflegenavi/web-components';
import { fmAppMainPages } from '@pflegenavi/frontend/routing';
import { YEAR_MONTH_FORMAT } from '@pflegenavi/shared/constants';
import { useTranslation } from 'react-i18next';
import { ResidentCard } from '../components/dashboard/ResidentCard';
import { InfoBox } from '../components/dashboard/InfoBox';
import { LatestFMTransactions } from '../components/dashboard/LatestFMTransactions';
import { LatestFMPayments } from '../components/dashboard/LatestFMPayment';
import { LatestPendingReceipts } from '../components/dashboard/LatestPendingReceipts';
// NOTE: Modal component is injected as prop to allow different implementations
// (e.g., ManualPaymentFormModal for Stripe, no-op component for MangoPay)
import type {
  DirectDebitStatus,
  GetTransactionsWithReceiptDto,
} from '@pflegenavi/shared/api';
import {
  ResidentState,
  ReceiptState,
  PaymentInitiatedBy,
} from '@pflegenavi/shared/api';
import { DownloadPdfButton } from '@pflegenavi/frontend/pdf-export';
import type {
  ResidentSummaryDto,
  ShowResidentDto,
} from '@pflegenavi/shared/api';
import type { PendingCharge } from '@pflegenavi/frontend/api-nursing-home';

// Extract PendingReceipt type from ShowResidentDto
type PendingReceipt = NonNullable<
  ShowResidentDto['data']['resident']['pending_receipts']
>[number];

// Interface for the modal component props
interface ModalComponentProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  activeBalance: number | undefined;
  isActiveBalanceLoading: boolean;
}

// Interface for resident data
interface ResidentData {
  id: string;
  state: ResidentState;
  firstName: string;
  lastName: string;
  balance: {
    amount: number;
    negativeDueDate: Date | null;
    thresholdDueDate: Date | null;
  };
}

// Interface for resident Phoenix data
interface ResidentPhoenixData {
  resident: {
    id: string;
    state: ResidentState;
    firstname: string;
    lastname: string;
    nursing_home: {
      name: string;
      base_data: {
        phone: string;
      };
    };
    nursing_home_residents: {
      entry_date: string;
    };
    created_on: string;
    pending_receipts?: PendingReceipt[];
  };
}

// Interface for PDF resident data
interface PdfResidentData {
  id: string;
  firstName: string;
  lastName: string;
  entry_date: Date;
  created_date: Date;
  nursingHome: {
    name: string;
  };
}

// Interface for LatestFMTransactions component props
interface LatestFMTransactionsComponentProps {
  isLoading: boolean;
  data: GetTransactionsWithReceiptDto[];
  onViewAll?: () => void;
  receiptImageUrls?: string[];
  imageUrlsLoading?: boolean;
  imageUrlToTypeMap?: any;
  receiptImageIds?: string[];
  onReceiptImageClick?: (imageIds: string[]) => void;
  onCloseImageGallery?: () => void;
}

// Interface for DownloadPdfButton component props
export interface DownloadPdfButtonComponentProps {
  getTransactionsResident: (params: {
    dateFrom: Date;
    dateTo: Date;
  }) => Promise<GetTransactionsWithReceiptDto[]>;
  resident: PdfResidentData | undefined;
  isLoadingResident: boolean;
  getResidentBalanceUntilDate: (dataIn: {
    params: {
      residentId: string;
      familyMemberId?: string | null;
      date: Date;
    };
  }) => Promise<{ residentId: string; balance: number }>;
  familyMemberId: string | null;
  pendingReceipts: PendingReceipt[];
  pendingPayments: PendingCharge[];
}

// Interface for InfoBox component props
export interface InfoBoxComponentProps {
  resident:
    | {
        id: string;
        state: ResidentState;
        nursingHome: {
          phone: string;
        };
        activeBalance?: {
          amount: number;
          thresholdDueDate: Date | null;
        };
      }
    | undefined;
  isResidentLoading: boolean;
  handleManualPayment: () => void;
  paymentByFamilyMember: boolean | undefined;
  directDebitStatus: DirectDebitStatus;
  initiatedBy: PaymentInitiatedBy | undefined;
  hasPendingCharge: boolean;
  paymentsDisabledForNursingHomeUntil: Date | undefined;
}

export interface DashboardPageViewProps {
  // Loading states
  isResidentPending: boolean;
  isSummaryLoading: boolean;
  isLoadingTransactions: boolean;
  paymentInfoLoading: boolean;
  paymentInfoPhoenixLoading: boolean;

  // Data props
  residentData?: ResidentData;
  residentResultPhoenix?: { data: ResidentPhoenixData };
  residentSummary?: ResidentSummaryDto;
  directDebit: DirectDebitStatus;
  paymentInitiation: PaymentInitiatedBy | undefined;
  paymentsDisabledUntil: Date | undefined;
  hasPendingCharges: boolean;
  pendingPayments: PendingCharge[];

  // Configuration props
  locale: string;
  activeBalanceId: string | null;
  familyMemberId: string | null;

  // Component injection props
  // Modal component (injected for different payment processors, optional for MangoPay)
  ModalComponent?: React.ComponentType<ModalComponentProps>;
  // LatestFMTransactions component (injected to allow view-only version in Storybook)
  LatestFMTransactionsComponent?: React.ComponentType<LatestFMTransactionsComponentProps>;
  // InfoBox component (injected to allow view-only version in Storybook)
  InfoBoxComponent?: React.ComponentType<InfoBoxComponentProps>;
  // DownloadPdfButton component (injected to allow view-only version in Storybook)
  DownloadPdfButtonComponent?: React.ComponentType<DownloadPdfButtonComponentProps>;

  // Modal state
  payNowModalOpen: boolean;
  setPayNowModalOpen: (open: boolean) => void;

  // Callback props
  handlePayButton: () => void;
  getResidentTransactions: (params: {
    dateFrom: Date;
    dateTo: Date;
  }) => Promise<GetTransactionsWithReceiptDto[]>;
  getResidentBalanceUntilDateAsFamilyMember: (params: {
    residentId: string;
    untilDate: Date;
  }) => Promise<{ balance: number }>;

  // UI state
  showPayButton: boolean;
  disablePayButton: boolean;
}

/**
 * DashboardPageView is a pure presentation component that displays the family member dashboard.
 * This component is separated from DashboardPage to enable better Storybook testing
 * by removing dependencies on React hooks and API calls.
 */
export const DashboardPageView: FC<DashboardPageViewProps> = ({
  // Loading states
  isResidentPending,
  isSummaryLoading,
  isLoadingTransactions,
  paymentInfoLoading,
  paymentInfoPhoenixLoading,

  // Data props
  residentData,
  residentResultPhoenix,
  residentSummary,
  directDebit,
  paymentInitiation,
  paymentsDisabledUntil,
  hasPendingCharges,
  pendingPayments,

  // Configuration props
  locale,
  activeBalanceId,
  familyMemberId,

  // Component injection props
  ModalComponent,
  LatestFMTransactionsComponent,
  InfoBoxComponent,
  DownloadPdfButtonComponent,

  // Modal state
  payNowModalOpen,
  setPayNowModalOpen,

  // Callback props
  handlePayButton,
  getResidentTransactions,
  getResidentBalanceUntilDateAsFamilyMember,

  // UI state
  showPayButton,
  disablePayButton,
}) => {
  const { t } = useTranslation();

  // Use injected component or default to container component
  const TransactionsComponent =
    LatestFMTransactionsComponent || LatestFMTransactions;
  const InfoBoxComponentToUse = InfoBoxComponent || InfoBox;
  const DownloadPdfButtonComponentToUse =
    DownloadPdfButtonComponent || DownloadPdfButton;

  // Transform resident data for InfoBox
  const infoBoxResident = residentResultPhoenix
    ? {
        id: residentResultPhoenix.data.resident.id,
        state: residentResultPhoenix.data.resident.state,
        nursingHome: {
          phone:
            residentResultPhoenix.data.resident.nursing_home.base_data.phone,
        },
        activeBalance: residentData?.balance
          ? {
              amount: residentData.balance.amount,
              thresholdDueDate: residentData.balance.thresholdDueDate,
            }
          : undefined,
      }
    : undefined;

  // Transform resident data for PDF export
  const pdfResidentData: PdfResidentData | undefined = residentResultPhoenix
    ? {
        id: residentResultPhoenix.data.resident.id,
        firstName: residentResultPhoenix.data.resident.firstname,
        lastName: residentResultPhoenix.data.resident.lastname,
        entry_date: new Date(
          residentResultPhoenix.data.resident.nursing_home_residents.entry_date
        ),
        created_date: new Date(residentResultPhoenix.data.resident.created_on),
        nursingHome: {
          name: residentResultPhoenix.data.resident.nursing_home.name,
        },
      }
    : undefined;

  return (
    <Container sx={{ maxWidth: 1400 }}>
      <HeaderBreadcrumbs
        heading={t('dashboard.main')}
        links={[
          { name: t('dashboard.main'), href: fmAppMainPages.DASHBOARD },
          { name: new Date().toLocaleDateString(locale, YEAR_MONTH_FORMAT) },
        ]}
        action={
          showPayButton ? (
            <div
              title={
                disablePayButton
                  ? t('fm-triggered-payments.pay-disabled-account-issue')
                  : ''
              }
            >
              <Button
                variant="contained"
                disabled={disablePayButton}
                onClick={handlePayButton}
              >
                {t('fm-triggered-payments.pay-now')}
              </Button>
            </div>
          ) : null
        }
      />
      {ModalComponent && (
        <ModalComponent
          key={payNowModalOpen.toString()}
          open={payNowModalOpen}
          setOpen={setPayNowModalOpen}
          activeBalance={residentData?.balance.amount}
          isActiveBalanceLoading={isResidentPending}
        />
      )}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={5}>
          <ResidentCard
            resident={residentData}
            nursingHomeName={
              residentResultPhoenix?.data.resident.nursing_home.name
            }
            directDebitStatus={directDebit}
            paymentInitiation={paymentInitiation}
            isResidentLoading={isResidentPending}
            isDirectDebitStatusLoading={
              paymentInfoLoading || paymentInfoPhoenixLoading
            }
          />
        </Grid>
        <Grid item xs={12} md={7}>
          <InfoBoxComponentToUse
            resident={infoBoxResident}
            isResidentLoading={isResidentPending}
            handleManualPayment={handlePayButton}
            paymentByFamilyMember={
              paymentInitiation === PaymentInitiatedBy.FamilyMember
            }
            directDebitStatus={directDebit}
            initiatedBy={paymentInitiation}
            hasPendingCharge={hasPendingCharges}
            paymentsDisabledForNursingHomeUntil={paymentsDisabledUntil}
          />
        </Grid>
      </Grid>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1, mt: 1 }}>
        <DownloadPdfButtonComponentToUse
          getTransactionsResident={getResidentTransactions}
          resident={pdfResidentData}
          isLoadingResident={isResidentPending}
          getResidentBalanceUntilDate={async (dataIn) => {
            const result = await getResidentBalanceUntilDateAsFamilyMember({
              residentId: dataIn.params.residentId,
              untilDate: dataIn.params.date,
            });
            return {
              residentId: dataIn.params.residentId,
              balance: result.balance,
            };
          }}
          familyMemberId={familyMemberId}
          pendingReceipts={
            residentResultPhoenix?.data.resident.pending_receipts ?? []
          }
          pendingPayments={pendingPayments}
        />
      </Stack>
      {/* Pending Receipts Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <LatestPendingReceipts
            receiptState={
              residentData?.state === ResidentState.awaitingSettlement
                ? ReceiptState.AwaitingSettlement
                : ReceiptState.Pending
            }
          />
        </Grid>
      </Grid>
      {/* Transactions and Payments Section */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <TransactionsComponent
            isLoading={isSummaryLoading}
            data={residentSummary?.latestTransactions ?? []}
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <LatestFMPayments
            isLoading={isSummaryLoading}
            data={residentSummary?.latestPayments ?? []}
            allPendingCharges={pendingPayments}
            nursingHomePhone={infoBoxResident?.nursingHome.phone}
            nursingHomeName={
              residentResultPhoenix?.data.resident.nursing_home.name
            }
          />
        </Grid>
      </Grid>
    </Container>
  );
};
